import { Auth } from './auth.js';
import * as Logger from './logger.js';

// Client for the tier-3 contextual "explain" API
// (docs/CONTEXTUAL_EXPLAINERS_DESIGN.md §5, handoff/frontend/tier3-explain-ui.md).
// Opposite caching rule from HelpRegistry: `explain()` is NEVER cached —
// every call is a fresh POST, because a response is only valid for the exact
// context it was computed from. Only the subjects list (static metadata, not
// computed-per-config) is kept in memory for the page session, same as
// HelpRegistry's snapshot.
class ExplainClientClass {
    #baseUrl = null;
    #subjects = null;
    #subjectsLoading = null;

    configure(baseUrl) {
        const next = baseUrl || '/elm/api';
        if (next !== this.#baseUrl) {
            this.#baseUrl = next;
            this.#subjects = null;
            this.#subjectsLoading = null;
        }
    }

    // Drives "what can be explained" (tier3-explain-ui.md task 1) — callers
    // check this before showing an explain affordance rather than hardcoding
    // which subjects exist; new server-side subjects need no frontend change
    // to become checkable here.
    async fetchSubjects() {
        if (this.#subjects) return this.#subjects;
        if (!this.#subjectsLoading) this.#subjectsLoading = this.#fetchSubjectsNow();
        return this.#subjectsLoading;
    }

    async #fetchSubjectsNow() {
        try {
            const res = await Auth.fetch({ path: `${this.#baseUrl || '/elm/api'}/explain/subjects` });
            this.#subjects = res?.subjects || [];
        } catch (err) {
            Logger.debug('Explain subjects fetch failed: ', err);
            this.#subjects = [];
        }
        this.#subjectsLoading = null;
        return this.#subjects;
    }

    async getSubject(name) {
        const subjects = await this.fetchSubjects();
        return subjects.find(s => s.name === name) || null;
    }

    // Never cached — a fresh POST every call (§5.1 "the load-bearing rule").
    // No client-side cache-front, no dedup-by-context: an identical context
    // requested twice still means two computations, because "identical" is
    // the caller's guess, not a guarantee.
    async explain(subject, context) {
        return await Auth.fetch({
            path: `${this.#baseUrl || '/elm/api'}/explain`,
            method: 'POST',
            payload: { subject, context },
        });
    }
}

export const ExplainClient = new ExplainClientClass();
